import { CaretRightIcon } from "@phosphor-icons/react/dist/csr/CaretRight";
import type { ReactNode, Ref } from "react";
import { Badge } from "@volca/ui/components/ui/badge";
import { Skeleton } from "@volca/ui/components/ui/skeleton";
import { Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";
import { isMissing, MISSING_LABEL } from "@/profitability/missing";
import type { ReportColumn, ReportValue } from "@/profitability/types";
import { figureHow, isNoMatch, missingWhy, partialWords } from "./figure-words";
import { figureDiffers, figureParts } from "./sourced";
import type { CitationsAnswer } from "./types";
import { useFigureFormat } from "./use-figure-format";

const Dot = () => (
  <span aria-hidden className="size-1.5 flex-none rounded-full bg-warning" />
);

const plural = (count: number, word: string) =>
  `${count} ${word}${count === 1 ? "" : "s"}`;

/** How the figure was made, or why it is missing, in a line. */
const howLine = (
  answer: CitationsAnswer,
  column: ReportColumn | null,
  roots: string[] | undefined,
): string | null => {
  const { figure, citations } = answer;
  if (!figure) {
    const rows = figureParts(answer).length;
    return rows ? `Cited from ${plural(rows, "row")}` : null;
  }
  return (
    missingWhy(figure, roots) ??
    figureHow(figure, !!citations[0] || !!column?.source)
  );
};

/**
 * One figure of the record: its name, the report's value (to the cent) and
 * how it was made ("Sum of 2 lines in Payroll export"); "Missing" with why
 * (a blank cell, every line blank, nothing matched); a quiet warning when
 * some of its lines were blank; "Differs from the file" only where the file
 * says otherwise. Its parts list under it when it is open (`children`).
 */
export const SourceFigureRow = ({
  ref,
  column,
  report,
  answer,
  loading,
  error,
  selected,
  focused,
  focusable,
  expanded,
  roots,
  action,
  onSelect,
  children,
}: {
  ref?: Ref<HTMLLIElement>;
  /** Null for the record as a whole (a cited dataset with no sourced column). */
  column: ReportColumn | null;
  report: ReportValue | undefined;
  answer: CitationsAnswer | undefined;
  loading: boolean;
  error: boolean;
  /** The figure, or one of its parts, is the one shown. */
  selected: boolean;
  /** The figure itself (not a part) is the one shown. */
  focused: boolean;
  focusable: boolean;
  /** Open, closed, or undefined for a figure with no parts. */
  expanded: boolean | undefined;
  /**
   * The missing figures a missing formula traces to, by name: "Can't be
   * worked out without Labor" rather than naming Burden, which is missing
   * only because Labor is.
   */
  roots?: string[];
  /** What to do about it: the upload, for a figure nothing matched. */
  action?: ReactNode;
  onSelect: () => void;
  /** Its parts, as a `role="group"` list. */
  children?: ReactNode;
}) => {
  const format = useFigureFormat();
  const label = column ? (column.label ?? column.key) : "The record";
  const figure = answer?.figure ?? null;
  const missing = !!column && isMissing(report ?? null);
  const how = answer ? howLine(answer, column, roots) : null;
  const partial = partialWords(figure);
  const differs = figureDiffers(figure, report, column);
  return (
    <li
      role="treeitem"
      aria-level={1}
      aria-selected={focused}
      aria-expanded={expanded}
      aria-label={label}
      tabIndex={focusable ? 0 : -1}
      className="group/figure outline-none"
      ref={ref}
    >
      <div
        onClick={onSelect}
        className={cn(
          // Calm, as the record sheet's own selections: a sunken surface and
          // a hairline, never a tint; the focus ring only from the keyboard.
          "grid cursor-pointer grid-cols-[1rem_minmax(0,1fr)_auto] items-baseline gap-x-1.5 gap-y-1 rounded-lg py-2.5 pr-3 pl-2 group-focus-visible/figure:ring-2 group-focus-visible/figure:ring-focus group-focus-visible/figure:ring-inset",
          {
            "bg-surface-sunken": selected,
            "ring-1 ring-line-strong ring-inset": focused,
            "hover:bg-neutral-subtle-hovered": !selected,
          },
        )}
      >
        <span className="flex h-full items-center self-center text-subtle">
          {expanded !== undefined && (
            <CaretRightIcon
              aria-hidden
              className={cn("size-3.5 transition-transform", {
                "rotate-90": expanded,
              })}
            />
          )}
        </span>
        <span className="flex min-w-0 items-center gap-2">
          <Text variant="label" truncate>
            {label}
          </Text>
          {differs && (
            <Badge variant="secondary" color="warning" size="sm">
              Differs from the file
            </Badge>
          )}
        </span>
        <Text render={<span />} tabular className="text-right">
          {!column ? null : missing ? (
            <span className="inline-flex items-center gap-1.5 text-subtle">
              <Dot />
              {MISSING_LABEL}
            </span>
          ) : (
            format.value(report, column)
          )}
        </Text>
        <Text
          variant="caption"
          tone="muted"
          render={<span />}
          className="col-span-2 col-start-2 min-w-0"
        >
          {loading ? (
            <Skeleton className="my-0.5 h-3.5 w-40" />
          ) : error ? (
            "How it was made could not be loaded"
          ) : (
            how
          )}
        </Text>
        {partial && (
          <Text
            variant="caption"
            render={<span />}
            className="col-span-2 col-start-2 inline-flex items-center gap-1.5 text-subtle"
          >
            <Dot />
            {partial}
          </Text>
        )}
        {action && isNoMatch(figure) && (
          <span className="col-span-2 col-start-2 pt-1">{action}</span>
        )}
      </div>
      {children}
    </li>
  );
};
